<script setup>
import { computed } from 'vue';

const props = defineProps({
  totalPages: {
    type: Number,
    required: true,
    default: 1
  },
  currentPage: {
    type: Number,
    required: true,
    default: 1
  },
  maxVisibleButtons: {
    type: Number,
    default: 5
  },
  size: {
    type: String,
    default: '', // '', 'sm', or 'lg'
    validator: (value) => ['', 'sm', 'lg'].includes(value)
  },
  align: {
    type: String,
    default: '', // '', 'center', or 'end'
    validator: (value) => ['', 'center', 'end'].includes(value)
  }
});

const emit = defineEmits(['page-changed']);

// Compute page numbers to be displayed
const pages = computed(() => {
  if (props.totalPages <= props.maxVisibleButtons) {
    return range(1, props.totalPages);
  }

  const halfWay = Math.floor(props.maxVisibleButtons / 2);
  
  if (props.currentPage <= halfWay) {
    return range(1, props.maxVisibleButtons);
  } else if (props.currentPage > props.totalPages - halfWay) {
    return range(props.totalPages - props.maxVisibleButtons + 1, props.totalPages);
  } else {
    return range(props.currentPage - halfWay, props.currentPage + halfWay);
  }
});

// Generate an array of numbers [start..end]
function range(start, end) {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

// Go to specific page
function goToPage(page) {
  if (page === props.currentPage || page < 1 || page > props.totalPages) {
    return;
  }
  
  emit('page-changed', page);
}

// Get classes for pagination container
const paginationClasses = computed(() => {
  const classes = ['pagination'];
  
  if (props.size) {
    classes.push(`pagination-${props.size}`);
  }
  
  return classes;
});

// Get classes for pagination container wrapper
const wrapperClasses = computed(() => {
  const classes = ['d-flex'];
  
  if (props.align === 'center') {
    classes.push('justify-content-center');
  } else if (props.align === 'end') {
    classes.push('justify-content-end');
  } else {
    classes.push('justify-content-start');
  }
  
  return classes;
});
</script>

<template>
  <nav aria-label="Page navigation" v-if="totalPages > 1">
    <div :class="wrapperClasses">
      <ul :class="paginationClasses">
        <!-- Previous button -->
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)" aria-label="Previous">
            <span aria-hidden="true">&laquo;</span>
          </a>
        </li>
        
        <!-- First page button when not visible in page range -->
        <li v-if="pages[0] > 1" class="page-item">
          <a class="page-link" href="#" @click.prevent="goToPage(1)">1</a>
        </li>
        
        <!-- Ellipsis if needed -->
        <li v-if="pages[0] > 2" class="page-item disabled">
          <span class="page-link">...</span>
        </li>
        
        <!-- Page numbers -->
        <li v-for="page in pages" :key="page" class="page-item" :class="{ active: page === currentPage }">
          <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
        </li>
        
        <!-- Ellipsis if needed -->
        <li v-if="pages[pages.length - 1] < totalPages - 1" class="page-item disabled">
          <span class="page-link">...</span>
        </li>
        
        <!-- Last page button when not visible in page range -->
        <li v-if="pages[pages.length - 1] < totalPages" class="page-item">
          <a class="page-link" href="#" @click.prevent="goToPage(totalPages)">{{ totalPages }}</a>
        </li>
        
        <!-- Next button -->
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)" aria-label="Next">
            <span aria-hidden="true">&raquo;</span>
          </a>
        </li>
      </ul>
    </div>
  </nav>
</template>

<style scoped>
.page-item.active .page-link {
  background-color: #2e7d32;
  border-color: #2e7d32;
}

.page-link {
  color: #2e7d32;
}

.page-link:hover {
  color: #1b5e20;
}
</style>